import { useEffect, useState } from 'react';
import { Button, Menu, MenuItem, ListSubheader, CircularProgress } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getMyClusters, switchCluster } from '../lib/clusters-api';
import { getSelectedClusterId } from '../lib/api-client';
import { isLegacyCompany } from '../lib/platform-api';
import type { ClusterSummary } from '@residenone/shared';
import { useT } from '../i18n';

export function ClusterSwitcher() {
  const t = useT();
  const [clusters, setClusters] = useState<ClusterSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getMyClusters()
      .then((r) => {
        setClusters(r.clusters);
        const storedId = getSelectedClusterId();
        setActiveId(storedId && r.clusters.some((cluster) => cluster.id === storedId)
          ? storedId : r.active_cluster_id);
      })
      .catch(() => undefined);
  }, []);

  if (clusters.length <= 1) return null; // CLUSTER-scope admins: hide entirely

  const active = clusters.find((c) => c.id === activeId);
  const multiDev = new Set(clusters.map((c) => c.developer_id)).size > 1;

  const onPick = async (id: string) => {
    if (id === activeId) {
      setAnchor(null);
      return;
    }
    setBusy(true);
    try {
      await switchCluster(id);
      window.location.reload();
    } catch {
      setBusy(false);
      setAnchor(null);
    }
  };

  const items: React.ReactNode[] = [];
  if (multiDev) {
    const byDev = new Map<string, ClusterSummary[]>();
    for (const c of clusters) {
      const key = isLegacyCompany(c.developer_id ?? '') ? t('plt.legacyName') : c.developer_name ?? 'Other';
      if (!byDev.has(key)) byDev.set(key, []);
      byDev.get(key)!.push(c);
    }
    for (const [dev, list] of byDev) {
      items.push(<ListSubheader key={`h-${dev}`}>{dev}</ListSubheader>);
      for (const c of list) {
        items.push(
          <MenuItem key={c.id} selected={c.id === activeId} onClick={() => onPick(c.id)}>
            {c.name}
          </MenuItem>,
        );
      }
    }
  } else {
    for (const c of clusters) {
      items.push(
        <MenuItem key={c.id} selected={c.id === activeId} onClick={() => onPick(c.id)}>
          {c.name}
        </MenuItem>,
      );
    }
  }

  return (
    <>
      <Button
        color="inherit"
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ApartmentIcon />}
        endIcon={<KeyboardArrowDownIcon />}
        sx={{ width: '100%', justifyContent: 'flex-start', textTransform: 'none' }}
        aria-label={t('cluster.switchAriaLabel')}
        disabled={busy}
      >
        {active?.name ?? t('cluster.fallback')}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {items}
      </Menu>
    </>
  );
}
